import { restoreIfMissing } from './trackingCore'

const STORAGE_KEY = 'kidneypal_attribution'
const EXPIRY_DAYS = 90

interface AttributionData {
  gclid?: string
  /** Google's iOS click ids: some iPhone clicks carry one of these instead of gclid. */
  gbraid?: string
  wbraid?: string
  fbclid?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  landingPage?: string
  capturedAt: number
}

/**
 * This page's attribution record (JSON) and the Meta cookies last seen, held
 * for the life of the page. iOS WebKit can drop the origin's storage while the
 * page is open (10-20 iPhone QA: a checkout after the drop reached Paddle with
 * no utm/gclid/fbc). Storage still wins whenever it has a value; the page copy
 * only fills in once it has gone missing, and is written back so a later
 * reload finds it (same idea as the variant pin, experimentCore.pinnedVariant).
 */
let pinned: string | null = null
const lastSeenCookie: Record<string, string> = {}

function localStore(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    return null
  }
}

export function captureAttribution() {
  const params = new URLSearchParams(window.location.search)

  const gclid = params.get('gclid')
  const gbraid = params.get('gbraid')
  const wbraid = params.get('wbraid')
  const fbclid = params.get('fbclid')
  const utm_source = params.get('utm_source')
  const utm_medium = params.get('utm_medium')
  const utm_campaign = params.get('utm_campaign')
  const utm_content = params.get('utm_content')
  const utm_term = params.get('utm_term')

  // Only store if we have at least one attribution param. Any explicit utm_*
  // counts (not just utm_source), and this runs independently of the variant
  // `ref` code: ad URLs carry BOTH `ref=<code>` and utm_source/medium/campaign
  // (plan §A4) — a `ref` alone sets no utm and leaves stored attribution as-is.
  if (!gclid && !gbraid && !wbraid && !fbclid && !utm_source && !utm_medium && !utm_campaign && !utm_content && !utm_term) {
    // No new click: pin what an earlier landing stored, if anything.
    const stored = getAttribution()
    pinned = stored ? JSON.stringify(stored) : null
    noteBrowserIds()
    return
  }

  const data: AttributionData = {
    ...(gclid && { gclid }),
    ...(gbraid && { gbraid }),
    ...(wbraid && { wbraid }),
    ...(fbclid && { fbclid }),
    ...(utm_source && { utm_source }),
    ...(utm_medium && { utm_medium }),
    ...(utm_campaign && { utm_campaign }),
    ...(utm_content && { utm_content }),
    ...(utm_term && { utm_term }),
    landingPage: window.location.pathname,
    capturedAt: Date.now(),
  }

  pinned = JSON.stringify(data)
  try {
    localStorage.setItem(STORAGE_KEY, pinned)
  } catch {
    // private mode / quota — the pin still holds for this page
  }

  const expires = new Date(Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000).toUTCString()

  // Google Ads click ID cookie (cross-subdomain)
  if (gclid) {
    document.cookie = `_gcl_aw=${gclid};expires=${expires};path=/;SameSite=Lax`
  }

  // Meta _fbc cookie — required format: fb.1.<timestamp>.<fbclid>
  // Meta's Pixel writes this automatically but only if the click is from facebook.com.
  // We force-write it for cross-domain fbclid clicks too.
  if (fbclid) {
    const fbcValue = `fb.1.${Date.now()}.${fbclid}`
    document.cookie = `_fbc=${fbcValue};expires=${expires};path=/;SameSite=Lax`
  }
  noteBrowserIds()
}

function parse(raw: string): AttributionData | null {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function isExpired(data: AttributionData): boolean {
  return Date.now() - data.capturedAt > EXPIRY_DAYS * 24 * 60 * 60 * 1000
}

export function getAttribution(): AttributionData | null {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    // storage unusable — fall back to the page copy below
  }

  if (raw) {
    const data = parse(raw)
    if (data && isExpired(data)) {
      try {
        localStorage.removeItem(STORAGE_KEY)
      } catch {}
      return null
    }
    return data
  }

  // Storage has nothing: this page's own record, written back for a reload.
  const data = pinned ? parse(pinned) : null
  if (!data || isExpired(data)) return null
  restoreIfMissing(localStore(), STORAGE_KEY, pinned)
  return data
}

export function getGclid(): string | undefined {
  return getAttribution()?.gclid
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`))
  if (!match) return undefined
  const value = match.split('=').slice(1).join('=')
  try {
    return decodeURIComponent(value)
  } catch {
    return value // malformed % sequence (e.g. a hand-edited fbclid) — this now runs at boot, so never throw
  }
}

/** The cookie now, else the last value this page saw (it went missing mid-page). */
function browserCookie(name: string): string | undefined {
  const value = readCookie(name)
  if (value) lastSeenCookie[name] = value
  return value || lastSeenCookie[name]
}

/** Meta's first-party browser ID cookie. Set by the Pixel on PageView. */
export function getFbp(): string | undefined {
  return browserCookie('_fbp')
}

/** Meta's click-through cookie. Set by the Pixel from fbclid (or by us for cross-domain). */
export function getFbc(): string | undefined {
  return browserCookie('_fbc')
}

/** Remember the Meta cookies as they are now (called on every funnel event). */
export function noteBrowserIds(): void {
  getFbp()
  getFbc()
}
